import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from '../App';
import Navigation from '../components/Navigation';
import ContributionGraph from '../components/ContributionGraph';
import ApplicationList from '../components/ApplicationList';

interface DashboardPageProps {
  user: User;
}

export default function DashboardPage({ user }: DashboardPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Sync stats to extension
  const syncStatsToExtension = useCallback(async () => {
    try {
      // Calculate stats
      const applicationsRef = collection(db, 'applications');
      const q = query(applicationsRef, where('userId', '==', user.uid));
      const snapshot = await getDocs(q);

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      let total = 0;
      let today = 0;
      let thisWeek = 0;

      snapshot.forEach((doc) => {
        total++;
        const data = doc.data();
        const appDate = data.appliedDate?.toDate?.() || new Date(data.appliedDate);

        if (appDate >= todayStart) today++;
        if (appDate >= weekAgo) thisWeek++;
      });

      const stats = { total, today, thisWeek };

      // Try to send to extension if installed
      const extensionId = localStorage.getItem('extensionId');
      if (extensionId && window.chrome?.runtime?.sendMessage) {
        window.chrome.runtime.sendMessage(extensionId, {
          type: 'SYNC_STATS',
          stats
        }, () => {
          // Ignore errors - extension might not be installed
        });
      }
    } catch (error) {
      console.error('Error syncing stats:', error);
    }
  }, [user.uid]);

  // Handle auto-save from extension
  useEffect(() => {
    const autoSave = searchParams.get('autoSave');
    const company = searchParams.get('company');
    const jobTitle = searchParams.get('jobTitle');

    if (autoSave === 'true' && company && jobTitle) {
      handleAutoSave();
    }

    // Store extension ID if provided
    const extensionId = searchParams.get('extensionId');
    if (extensionId) {
      localStorage.setItem('extensionId', extensionId);
    }

    // Sync stats on load
    syncStatsToExtension();
  }, [searchParams, syncStatsToExtension]);

  const handleAutoSave = async () => {
    setAutoSaveStatus('saving');

    try {
      const company = searchParams.get('company') || '';
      const jobTitle = searchParams.get('jobTitle') || '';
      const location = searchParams.get('location') || '';
      const jobUrl = searchParams.get('jobUrl') || '';

      // Create the application
      await addDoc(collection(db, 'applications'), {
        userId: user.uid,
        company,
        jobTitle,
        location,
        jobUrl,
        status: 'applied',
        statusHistory: [{
          status: 'applied',
          timestamp: Timestamp.now(),
        }],
        source: 'extension',
        appliedDate: Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      setAutoSaveStatus('saved');

      // Clear URL params after successful save
      setSearchParams({});

      // Sync stats after adding
      syncStatsToExtension();

      // Reset status after a delay
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error auto-saving application:', error);
      setAutoSaveStatus('error');
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleClearDateFilter = () => {
    setSelectedDate(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation user={user} />

      {/* Auto-save notification */}
      {autoSaveStatus !== 'idle' && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          autoSaveStatus === 'saving' ? 'bg-blue-500 text-white' :
          autoSaveStatus === 'saved' ? 'bg-green-500 text-white' :
          'bg-red-500 text-white'
        }`}>
          {autoSaveStatus === 'saving' && 'Saving application...'}
          {autoSaveStatus === 'saved' && '✓ Application saved from extension!'}
          {autoSaveStatus === 'error' && '✗ Failed to save application'}
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-6 sm:space-y-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Track your job applications and monitor your progress
            </p>
          </div>

          <ContributionGraph userId={user.uid} onDateSelect={handleDateSelect} />

          <ApplicationList userId={user.uid} selectedDate={selectedDate} onClearDateFilter={handleClearDateFilter} />
        </div>
      </main>

      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
            © 2024 Job Application Tracker. Track your career journey.
          </p>
        </div>
      </footer>
    </div>
  );
}
